import React from 'react';

/**
 * A reusable Image component that handles backend URLs and fallback to mickey-head image
 * 
 * @param {Object} props - Component props
 * @param {string} props.src - Source URL for the image (supports backend relative paths)
 * @param {string} props.alt - Alt text for the image
 * @param {string} props.className - Optional CSS class
 * @param {Object} props.style - Optional inline styles
 * @param {Function} props.onClick - Optional click handler
 * @returns {React.Component} - Image component with fallback and backend URL support
 */
const ImageComponent = ({ src, alt, className, style, onClick, ...rest }) => {
  const getBackendBaseUrl = () => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    return apiUrl.replace(/\/api$/, '');
  };

  const getImageUrl = (imageSrc) => {
    if (!imageSrc) return '/assets/images/mickey-head.png';
    if (imageSrc.startsWith('http')) return imageSrc;
    if (imageSrc.startsWith('/assets')) return imageSrc;
    if (imageSrc.startsWith('/uploads')) return `${getBackendBaseUrl()}${imageSrc}`;
    if (!imageSrc.startsWith('/')) return `${getBackendBaseUrl()}/uploads/${imageSrc}`;
    return `${getBackendBaseUrl()}${imageSrc}`;
  };

  // Handle image loading errors
  const handleError = (e) => {
    e.target.onerror = null; // Prevent infinite loops
    
    // If the original src was a backend URL, try the fallback
    if (src && (src.startsWith('/uploads') || src.includes('/uploads'))) {
      console.warn(`Failed to load backend image: ${src}, using fallback`);
    }
    
    e.target.src = '/assets/images/mickey-head.png';
  };

  const imageUrl = getImageUrl(src);

  return (
    <img
      src={imageUrl}
      alt={alt || 'Disney image'}
      className={className}
      style={style}
      onClick={onClick}
      onError={handleError}
      loading="lazy" // Add lazy loading for better performance
      {...rest}
    />
  );
};

export default ImageComponent;